/* Offline-Outbox für Wartungen inkl. Fotos.
   Reihenfolge: Wartung wird zuerst am Server angelegt (sofort in "Wartung bearbeiten" sichtbar),
   danach werden die Fotos einzeln nachgereicht und per PATCH angehängt.
   Fotos liegen als ArrayBuffer in IndexedDB (iOS/Safari kann gespeicherte Blobs verlieren). */
import api from "@/api/client";
import { addEntry, putEntry, deleteEntry, getEntries, getEntry } from "@/lib/offlineDb";

export const OUTBOX_EVENT = "mps-outbox-changed";

const notify = (detail = {}) => window.dispatchEvent(new CustomEvent(OUTBOX_EVENT, { detail }));

let syncing = false;
let syncStartedAt = 0;
let intervalId = null;
const STUCK_AFTER_MS = 180000;

export const getOutbox = getEntries;

export const pendingCount = async () => (await getEntries()).length;

/** Übertragungsstand je Outbox-Eintrag inkl. Server-ID (für Anzeige im Bearbeiten-Formular). */
export const getOutboxProgress = async () => {
  const entries = await getEntries();
  return entries.map((e) => {
    const copy = { ...e };
    loadProgress(copy);
    return {
      id: copy.id,
      server_id: copy.server_id || null,
      uploaded: copy.uploaded || 0,
      total: (copy.photos || []).length,
      status: copy.status,
      error: copy.error || null,
      name: copy.payload?.name || "",
    };
  });
};

/** Wartung in die Outbox legen (Fotos als ArrayBuffer) und – wenn online – direkt senden. */
export const queueWartung = async ({ payload, photos }) => {
  const stored = [];
  for (const f of photos || []) {
    stored.push({
      name: f.name || `foto-${stored.length + 1}.jpg`,
      type: f.type || "image/jpeg",
      buffer: await f.arrayBuffer(),
    });
  }
  const entry = {
    id: `w-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: "wartung",
    payload,
    photos: stored,
    thumbnails: [],
    originals: [],
    uploaded: 0,
    server_id: null,
    status: "pending",
    error: null,
    attempts: 0,
    created_at: new Date().toISOString(),
  };
  try {
    await addEntry(entry);
  } catch (err) {
    if (err?.name === "QuotaExceededError" || /quota/i.test(err?.message || "")) {
      throw new Error("Speicher auf dem Gerät voll. Bitte zuerst bestehende Wartungen übertragen (Internet suchen) und es dann erneut versuchen.");
    }
    throw err;
  }
  notify({ queued: entry.id });
  if (navigator.onLine) syncOutbox();
  return entry;
};

const photoBlob = async (photo) => {
  if (photo.buffer) return new Blob([photo.buffer], { type: photo.type || "image/jpeg" });
  if (photo.blob) {
    // iOS kann in IndexedDB gespeicherte Blobs verlieren -> Lesbarkeit prüfen
    await photo.blob.slice(0, 1).arrayBuffer();
    return photo.blob;
  }
  return null;
};

/** Fehler lesbar machen (inkl. HTTP-Status und Validierungsdetails). */
export const describeError = (err) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  let msg = "";
  if (typeof detail === "string") msg = detail;
  else if (Array.isArray(detail)) msg = detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  else if (detail) msg = JSON.stringify(detail).slice(0, 200);
  if (!msg && err?.code === "ERR_NETWORK") msg = "Keine Verbindung zum Server";
  if (!msg) msg = err?.message || "unbekannter Fehler";
  const url = err?.config?.url ? ` ${err.config.url}` : "";
  return status ? `${msg} (HTTP ${status}${url})` : `${msg}${url}`;
};

const uploadPhoto = async (photo) => {
  const blob = await photoBlob(photo);
  if (!blob) return null;
  const formData = new FormData();
  formData.append("file", blob, photo.name || "foto.jpg");
  formData.append("kategorie", "wartung");
  const res = await api.post("/upload-file", formData, { timeout: 120000 });
  return {
    thumbnail: res.data.thumbnail || res.data.preview || res.data.original || res.data.url,
    original: res.data.original || res.data.url,
  };
};

/* Fortschritt zusätzlich in localStorage sichern: Wenn das Schreiben in IndexedDB scheitert
   (iOS kann gespeicherte Blobs unklonbar machen), bleiben server_id und Upload-Stand erhalten. */
const progressKey = (id) => `mps-outbox-progress-${id}`;

const loadProgress = (entry) => {
  try {
    const raw = localStorage.getItem(progressKey(entry.id));
    if (!raw) return;
    const p = JSON.parse(raw);
    entry.server_id = entry.server_id || p.server_id || null;
    if ((p.uploaded || 0) > (entry.uploaded || 0)) {
      entry.uploaded = p.uploaded;
      entry.thumbnails = p.thumbnails || entry.thumbnails;
      entry.originals = p.originals || entry.originals;
      entry.lost = p.lost || entry.lost;
    }
  } catch (err) {
    /* defekter Eintrag wird ignoriert */
  }
};

const saveProgress = async (entry) => {
  try {
    localStorage.setItem(
      progressKey(entry.id),
      JSON.stringify({
        server_id: entry.server_id,
        uploaded: entry.uploaded,
        thumbnails: entry.thumbnails,
        originals: entry.originals,
        lost: entry.lost || 0,
      })
    );
  } catch (err) {
    /* localStorage voll – Fortschritt nur im Speicher */
  }
  try {
    await putEntry(entry);
  } catch (err) {
    /* Eintrag nicht klonbar (toter Blob) – Fortschritt liegt in localStorage */
  }
};

/* Server-IDs der zuletzt angelegten Wartungen (für direkten Sprung ins Bearbeiten-Formular). */
const createdIds = {};

/** Wartet darauf, dass die Wartung am Server angelegt wurde, und gibt ihre ID zurück. */
export const waitForServerId = async (entryId, timeoutMs = 25000) => {
  const bis = Date.now() + timeoutMs;
  while (Date.now() < bis) {
    if (createdIds[entryId]) return createdIds[entryId];
    try {
      const raw = localStorage.getItem(progressKey(entryId));
      if (raw) {
        const p = JSON.parse(raw);
        if (p.server_id) return p.server_id;
      }
    } catch (err) {
      /* ignorieren */
    }
    const entry = await getEntry(entryId);
    if (entry?.server_id) return entry.server_id;
    if (entry?.status === "error") return null;
    await new Promise((r) => setTimeout(r, 400));
  }
  return null;
};

const dropProgress = (id) => {
  try {
    localStorage.removeItem(progressKey(id));
  } catch (err) {
    /* ignorieren */
  }
};

const syncEntry = async (entry) => {
  loadProgress(entry);
  entry.status = "syncing";
  entry.error = null;
  await saveProgress(entry);
  notify({ syncing: entry.id });

  // 1. Wartung anlegen (idempotent über client_id) – damit sie sofort auffindbar ist
  if (!entry.server_id) {
    const res = await api.post("/wartungen", {
      ...entry.payload,
      client_id: entry.id,
      bilder: entry.thumbnails,
      bilder_original: entry.originals,
    });
    entry.server_id = res.data?.id;
    createdIds[entry.id] = entry.server_id;
    await saveProgress(entry);
    notify({ created: entry.id, server_id: entry.server_id });
  }

  // 2. Fotos einzeln hochladen und an die Wartung anhängen
  for (let i = entry.uploaded; i < entry.photos.length; i++) {
    let urls = null;
    try {
      urls = await uploadPhoto(entry.photos[i]);
    } catch (err) {
      const status = err?.response?.status;
      // Nicht wiederholbar (Datei kaputt/zu groß/nicht mehr lesbar) -> Foto überspringen,
      // damit die Wartung nicht dauerhaft blockiert. Alles andere erneut versuchen.
      const skippable = !err?.response || [400, 413, 415, 422].includes(status);
      const networkish = err?.code === "ERR_NETWORK" || err?.code === "ECONNABORTED" || /timeout|Network/i.test(err?.message || "");
      if (!skippable || networkish) throw err;
      entry.photo_errors = [...(entry.photo_errors || []), describeError(err)];
      urls = null;
    }
    if (!urls) {
      entry.uploaded = i + 1;
      entry.lost = (entry.lost || 0) + 1;
      await saveProgress(entry);
      notify({ progress: entry.id, uploaded: entry.uploaded, total: entry.photos.length });
      continue;
    }
    entry.thumbnails.push(urls.thumbnail);
    entry.originals.push(urls.original);
    entry.uploaded = i + 1;
    await saveProgress(entry);
    await api.patch(`/wartungen/${entry.server_id}`, {
      bilder: entry.thumbnails,
      bilder_original: entry.originals,
    });
    notify({ progress: entry.id, uploaded: entry.uploaded, total: entry.photos.length });
  }

  const lost = entry.lost || 0;
  const serverId = entry.server_id;
  await deleteEntry(entry.id);
  dropProgress(entry.id);
  notify({ done: entry.id, server_id: serverId, lost, name: entry.payload?.name });
  return lost;
};

/** Überträgt alle wartenden Einträge der Outbox. Läuft nie parallel. */
export const syncOutbox = async () => {
  if (!navigator.onLine) return { synced: 0, failed: 0, skipped: true };
  if (syncing && Date.now() - syncStartedAt < STUCK_AFTER_MS) return { synced: 0, failed: 0, skipped: true };
  syncing = true;
  syncStartedAt = Date.now();
  let synced = 0;
  let failed = 0;
  let lost = 0;
  try {
    const entries = await getEntries();
    for (const stored of entries) {
      const entry = (await getEntry(stored.id)) || stored;
      try {
        lost += await syncEntry(entry);
        synced += 1;
      } catch (err) {
        entry.attempts = (entry.attempts || 0) + 1;
        entry.status = "error";
        entry.error = describeError(err);
        await saveProgress(entry);
        failed += 1;
        notify({ error: entry.id });
        if (!navigator.onLine) break;
      }
    }
  } finally {
    syncing = false;
    notify({ finished: true, lost });
  }
  return { synced, failed, lost };
};

export const retryEntry = async (id) => {
  const entry = await getEntry(id);
  if (!entry) return;
  entry.status = "pending";
  entry.error = null;
  await saveProgress(entry);
  notify({ retry: id });
  return syncOutbox();
};

export const removeEntry = async (id) => {
  await deleteEntry(id);
  dropProgress(id);
  notify({ removed: id });
};

/** Event-Listener + regelmäßiger Sync-Versuch (iOS hat kein echtes Background-Sync). */
export const initOfflineSync = () => {
  window.addEventListener("online", () => syncOutbox());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") syncOutbox();
  });
  if (!intervalId) {
    intervalId = setInterval(() => {
      if (navigator.onLine) syncOutbox();
    }, 60000);
  }
  syncOutbox();
};
